import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Info, Trash2, X } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { CalendarDateRangePicker } from "@/components/calendar-date-range-picker";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";

const EXPERIENCE_LEVELS = ["Entry", "Intermediate", "Expert"];
const CATEGORIES = [
  "Web Development",
  "Mobile App Development",
  "UI/UX Design",
  "Graphic Design",
  "Content Writing",
  "Digital Marketing",
  "Data Analysis",
  "Video Editing",
  "Audio Production",
  "AI & Machine Learning",
  "Blockchain Development",
  "Human Resources",
  "Full-Stack Development",
  "Other",
];

export const FreelancerProfessionalForm = () => {
  const {
    register,
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "workExperience",
  });

  const {
    fields: projectFields,
    append: appendProject,
    remove: removeProject,
  } = useFieldArray({
    control,
    name: "projects",
  });

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center">
          <Label
            htmlFor="designation"
            className="font-medium text-sm text-gray-700"
          >
            Designation <span className="text-red-600">*</span>
          </Label>
          <div className="relative group ml-2">
            <Info className="h-4 w-4 text-gray-400" />
            <div className="hidden group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
              Your primary role or job title.
            </div>
          </div>
        </div>
        <Input
          id="designation"
          placeholder="e.g., Senior Web Developer"
          {...register("designation")}
          className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
        />
        {errors.designation && (
          <p className="text-xs text-red-500">
            {errors.designation.message as string}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center">
          <Label
            htmlFor="category"
            className="font-medium text-sm text-gray-700"
          >
            Category <span className="text-red-600">*</span>
          </Label>
          <div className="relative group ml-2">
            <Info className="h-4 w-4 text-gray-400" />
            <div className="hidden group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
              Your primary area of expertise.
            </div>
          </div>
        </div>
        <Controller
          name="category"
          control={control}
          render={({ field }) => (
            <Select
              onValueChange={(value) => {
                field.onChange(value);
              }}
              value={field.value}
            >
              <SelectTrigger
                id="category"
                className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
              >
                <SelectValue placeholder="Select a category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
        {errors.category && (
          <p className="text-xs text-red-500">
            {errors.category.message as String}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center">
          <Label
            htmlFor="experience"
            className="font-medium text-sm text-gray-700"
          >
            Experience
          </Label>
          <div className="relative group ml-2">
            <Info className="h-4 w-4 text-gray-400" />
            <div className="hidden group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
              Your expirence in years.
            </div>
          </div>
        </div>
        <Input
          id="experience"
          type="number"
          placeholder="experience in years"
          {...register("experience", { valueAsNumber: true })}
          className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
        />
        {errors.experience && (
          <p className="text-xs text-red-500">
            {errors.experience.message as string}
          </p>
        )}
      </div>

      {/* New Projects Section */}
      <div className="space-y-2">
        <div className="flex items-center">
          <Label className="font-medium text-sm text-gray-700">Projects</Label>
          <div className="relative group ml-2">
            <Info className="h-4 w-4 text-gray-400" />
            <div className="hidden group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
              Add details about your projects, including name, description,
              technologies used, role, and duration.
            </div>
          </div>
        </div>
        {projectFields.map((field, index) => (
          <div
            key={field.id}
            className="border p-6 rounded-md space-y-2 relative"
          >
            <div className="space-y-2">
              <Label htmlFor={`projects.${index}.name`}>Project Name <span className="text-red-600">*</span></Label>
              <Input
                id={`projects.${index}.name`}
                placeholder="e.g., AmazonCart"
                {...register(`projects.${index}.name`)}
                className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
              />
              {errors.projects?.[index]?.name && (
                <p className="text-xs text-red-500">
                  {errors.projects[index].name.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor={`projects.${index}.description`}>
                Description <span className="text-red-600">*</span>
              </Label>
              <Input
                id={`projects.${index}.description`}
                placeholder="e.g., Built a single web page app using React..."
                {...register(`projects.${index}.description`)}
                className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
              />
              {errors.projects?.[index]?.description && (
                <p className="text-xs text-red-500">
                  {errors.projects[index].description.message}
                </p>
              )}
            </div>
            <div>
              <FieldArrayTechnologies
                control={control}
                register={register}
                errors={errors}
                projectIndex={index}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`projects.${index}.role`}>Role <span className="text-red-600">*</span></Label>
              <Input
                id={`projects.${index}.role`}
                placeholder="e.g., Lead Developer"
                {...register(`projects.${index}.role`)}
                className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
              />
              {errors.projects?.[index]?.role && (
                <p className="text-xs text-red-500">
                  {errors.projects[index].role.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor={`projects.${index}.projectDuration`}>
                Project Duration <span className="text-red-600">*</span>
              </Label>
              <Controller
                control={control}
                name={`projects.${index}.projectDuration`}
                render={({ field }) => (
                  <CalendarDateRangePicker
                    dateRange={field.value || { start: "", end: "" }}
                    setDateRange={(value) => field.onChange(value)}
                  />
                )}
              />
              {errors.projects?.[index]?.projectDuration && (
                <p className="text-xs text-red-500">
                  {errors.projects[index]?.projectDuration?.start?.message ||
                    errors.projects[index]?.projectDuration?.end?.message ||
                    "Please provide valid start and end dates."}
                </p>
              )}
            </div>
            <div className="absolute right-6 top-0 m-0">
              {/* {projectFields.length > 1 && ( */}
              <Button
                type="button"
                size="icon"
                variant="destructive"
                onClick={() => removeProject(index)}
              >
                <Trash2 className="h-2 w-2" />
              </Button>
              {/* )} */}
            </div>
          </div>
        ))}
        <Button
          type="button"
          onClick={() =>
            appendProject({
              name: "",
              description: "",
              technologiesUsed: [],
              role: "",
              projectDuration: { start: "", end: "" },
            })
          }
          className="bg-emerald-500 hover:bg-emerald-600"
        >
          Add Project
        </Button>
      </div>

      <div className="space-y-2">
        <div className="flex items-center">
          <Label
            htmlFor="projectsCompleted"
            className="font-medium text-sm text-gray-700"
          >
            Projects Completed
          </Label>
          <div className="relative group ml-2">
            <Info className="h-4 w-4 text-gray-400" />
            <div className="hidden group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
              Number of projects successfully completed.
            </div>
          </div>
        </div>
        <Input
          id="projectsCompleted"
          type="number"
          placeholder="e.g., 50"
          {...register("projectsCompleted", { valueAsNumber: true })}
          className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
        />
        {errors.projectsCompleted && (
          <p className="text-xs text-red-500">
            {errors.projectsCompleted.message as string}
          </p>
        )}
      </div>

      {/* New Work Experience */}
      <div className="space-y-2">
        <div className="flex items-center">
          <Label className="font-medium text-sm text-gray-700">
            Work Experience
          </Label>
          <div className="relative group ml-2">
            <Info className="h-4 w-4 text-gray-400" />
            <div className="hidden group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
              Add your professional work experience, including job title,
              company, dates, and responsibilities.
            </div>
          </div>
        </div>

        {fields.map((field, index) => (
          <div
            key={field.id}
            className="border p-6 rounded-md space-y-4 relative"
          >
            <div className="space-y-2">
              <Label htmlFor={`workExperience.${index}.title`}>Job Title <span className="text-red-600">*</span></Label>
              <Input
                id={`workExperience.${index}.title`}
                placeholder="e.g., Senior Node.js Developer"
                {...register(`workExperience.${index}.title`)}
                className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
              />
              {errors.workExperience?.[index]?.title && (
                <p className="text-xs text-red-500">
                  {errors.workExperience[index].title.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor={`workExperience.${index}.company`}>Company <span className="text-red-600">*</span></Label>
              <Input
                id={`workExperience.${index}.company`}
                placeholder="e.g., ABC Tech Solutions"
                {...register(`workExperience.${index}.company`)}
                className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
              />
              {errors.workExperience?.[index]?.company && (
                <p className="text-xs text-red-500">
                  {errors.workExperience[index].company.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor={`workExperience.${index}.dates`}>Dates needs to be filled manually <span className="text-red-600">*</span></Label>
              <Controller
                control={control}
                name={`workExperience.${index}.dates`}
                render={({ field }) => {
                  return (
                    <CalendarDateRangePicker
                      dateRange={field.value || { start: "", end: "" }}
                      setDateRange={(value) => {
                        field.onChange(value);
                      }}
                    />
                  );
                }}
              />
              {errors.workExperience?.[index]?.dates && (
                <p className="text-xs text-red-500">
                  {errors.workExperience[index]?.dates?.start?.message ||
                    errors.workExperience[index]?.dates?.end?.message ||
                    "Please provide valid start and end dates."}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Responsibilities</Label>
              <FieldArrayResponsibilities
                control={control}
                register={register}
                errors={errors}
                workIndex={index}
              />
              <div className="absolute right-0 top-0 m-5">
                {/* {fields.length > 1 && ( */}
                <Button
                  type="button"
                  size="icon"
                  variant="destructive"
                  onClick={() => remove(index)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
                {/* )} */}
              </div>
            </div>
          </div>
        ))}

        {errors.workExperience && (
          <p className="text-xs text-red-500">
            {typeof errors.workExperience.message === "string" &&
              errors.workExperience.message}
          </p>
        )}

        <Button
          type="button"
          onClick={() =>
            append({
              title: "",
              company: "",
              dates: "",
              responsibilities: [""],
            })
          }
          className="bg-emerald-500 hover:bg-emerald-600"
        >
          Add Work Experience
        </Button>
      </div>

      <div className="space-y-2">
        <div className="flex items-center">
          <Label
            htmlFor="dailyRate"
            className="font-medium text-sm text-gray-700"
          >
            Daily rate
          </Label>
          <div className="relative group ml-2">
            <Info className="h-4 w-4 text-gray-400" />
            <div className="hidden group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
              Your daily rate in USD.
            </div>
          </div>
        </div>
        <Input
          id="dailyRate"
          type="number"
          placeholder="e.g., 10000"
          {...register("dailyRate", { valueAsNumber: true })}
          className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
        />
        {errors.dailyRate && (
          <p className="text-xs text-red-500">
            {errors.dailyRate.message as string}
          </p>
        )}
      </div>
    </div>
  );
};

const FieldArrayResponsibilities = ({
  control,
  register,
  errors,
  workIndex,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: `workExperience.${workIndex}.responsibilities`,
  });

  return (
    <div className="space-y-2">
      {fields.map((field, resIndex) => (
        <div key={field.id} className="flex items-center space-x-2">
          <Textarea
            id={`workExperience.${workIndex}.responsibilities.${resIndex}`}
            placeholder="e.g., Built scalable REST APIs"
            {...register(
              `workExperience.${workIndex}.responsibilities.${resIndex}`
            )}
            className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
          />
          {fields.length > 1 && (
            <Button
              type="button"
              className="border-red-100 text-red-500 hover:bg-red-50"
              size="icon"
              variant="outline"
              onClick={() => remove(resIndex)}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
          {errors.workExperience?.[workIndex]?.responsibilities?.[resIndex] && (
            <p className="text-xs text-red-500">
              {
                errors.workExperience[workIndex].responsibilities[resIndex]
                  .message
              }
            </p>
          )}
        </div>
      ))}
      <Button
        type="button"
        onClick={() => append("")}
        className="bg-emerald-500 hover:bg-emerald-600"
      >
        +
      </Button>
    </div>
  );
};

export const FieldArrayTechnologies = ({
  control,
  register,
  errors,
  projectIndex,
}) => {
  const { setValue, watch } = useFormContext();
  const technologies = watch(`projects.${projectIndex}.technologiesUsed`) || [];
  const [techInput, setTechInput] = useState("");

  const handleAddTech = () => {
    if (techInput) {
      const newTechnologies = techInput
        .split(",")
        .map((tech) => tech.trim())
        .filter((tech) => tech.length > 0 && !technologies.includes(tech));

      if (newTechnologies.length > 0) {
        setValue(`projects.${projectIndex}.technologiesUsed`, [
          ...technologies,
          ...newTechnologies,
        ]);
        setTechInput("");
      }
    }
  };

  const handleRemoveTech = (tech: string) => {
    setValue(
      `projects.${projectIndex}.technologiesUsed`,
      technologies.filter((s: string) => s !== tech)
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center">
        <Label className="font-medium text-sm text-gray-700">
          Technologies Used <span className="text-red-600">*</span>
        </Label>
        <div className="relative group ml-2">
          <Info className="h-4 w-4 text-gray-400" />
          <div className="hidden group-hover:block absolute -top-1 left-6 p-2 w-60 text-xs text-gray-600 bg-white rounded shadow-md z-10">
            Add technologies used in this project (e.g., React, Python).
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <Input
          id="techInput"
          placeholder="e.g., React, Python"
          value={techInput}
          onChange={(e) => setTechInput(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleAddTech()}
          className="border-blue-100 focus:border-emerald-300 focus:ring-1 focus:ring-emerald-300"
        />
        <Button
          type="button"
          onClick={handleAddTech}
          className="px-4 py-2 bg-emerald-500 text-white rounded hover:bg-emerald-600"
        >
          Add
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {technologies.map((tech: string) => (
          <Badge
            key={tech}
            variant="secondary"
            className="bg-sky-100 text-sky-700 flex items-center gap-1"
          >
            {tech}
            <X
              className="w-3 h-3 cursor-pointer"
              onClick={() => handleRemoveTech(tech)}
            />
          </Badge>
        ))}
      </div>
      {errors.projects?.[projectIndex]?.technologiesUsed && (
        <p className="text-xs text-red-500">
          {errors.projects[projectIndex].technologiesUsed.message ||
            "At least one technology is required"}
        </p>
      )}
    </div>
  );
};
